import React, { useState, useEffect, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Slider } from '@/components/ui/slider';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Loader2, ZoomIn, ZoomOut, Maximize2, TrendingUp, DollarSign, Target, ArrowRight, Package, MessageSquare, Search as SearchIcon, Sparkles, AlertTriangle } from 'lucide-react';
import { authFetch } from '@/utils/auth-fetch';

interface Position {
  x: number;
  y: number;
}

interface Question {
  id: number;
  text: string;
  type: string;
  search_volume: number;
  cpc: number;
  opportunity_score: number;
  difficulty_score: number;
  has_featured_snippet: boolean;
  has_paa: boolean;
  answer_type: string;
  answer_type_confidence: number;
  similarity: number;
  position: Position;
}

interface Product {
  id: string;
  name: string;
  description: string;
  price?: number;
  image_url?: string;
  brand?: string;
  has_embedding: boolean;
}

interface SemanticMapData {
  center: Question | {
    id: string;
    name: string;
    description: string;
    centerType: 'product';
    position: Position;
  };
  questions: Question[];
  metadata: {
    wheelId: string;
    centerType: 'question' | 'product';
    centerTerm: string;
    totalQuestions: number;
    avgSimilarity: number;
  };
}

interface SemanticMapProps {
  wheelId: string;
  siteId: string;
  sectorId?: string;  // Optional: if provided, uses sector endpoint
  centerTerm: string;
  sourceFilter?: string;
  answerTypeFilter?: string;
  onQuestionClick?: (question: Question) => void;
}

export const SemanticMap: React.FC<SemanticMapProps> = ({
  wheelId,
  siteId,
  sectorId,
  centerTerm,
  sourceFilter = 'all',
  answerTypeFilter = 'all',
  onQuestionClick
}) => {
  const navigate = useNavigate();
  const [data, setData] = useState<SemanticMapData | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [zoom, setZoom] = useState(1);
  const [hoveredQuestion, setHoveredQuestion] = useState<Question | null>(null);
  const [selectedQuestion, setSelectedQuestion] = useState<Question | null>(null);
  const [centerQuestionId, setCenterQuestionId] = useState<number | null>(null);
  const [similarityThreshold, setSimilarityThreshold] = useState(0);

  // Phase 3: Product as center state
  const [centerType, setCenterType] = useState<'question' | 'product'>('question');
  const [selectedProduct, setSelectedProduct] = useState<Product | null>(null);
  const [productSearchQuery, setProductSearchQuery] = useState('');
  const [productSearchResults, setProductSearchResults] = useState<Product[]>([]);
  const [searchingProducts, setSearchingProducts] = useState(false);
  const [creatingProductWheel, setCreatingProductWheel] = useState(false);

  // Embedding generation state
  const [generatingEmbeddings, setGeneratingEmbeddings] = useState(false);
  const [embeddingStats, setEmbeddingStats] = useState<{
    totalQuestions: number;
    withEmbeddings: number;
    withoutEmbeddings: number;
    percentage: number;
  } | null>(null);

  const svgWidth = 800;
  const svgHeight = 600;
  const centerX = svgWidth / 2;
  const centerY = svgHeight / 2;

  // Product search functionality
  useEffect(() => {
    if (centerType !== 'product' || productSearchQuery.length < 2) {
      setProductSearchResults([]);
      return;
    }

    const timeoutId = setTimeout(async () => {
      setSearchingProducts(true);
      try {
        const response = await authFetch(
          `/api/sites/${siteId}/products/search?query=${encodeURIComponent(productSearchQuery)}&limit=20`
        );
        const data = await response.json();
        setProductSearchResults(data.products || []);
      } catch (error) {
        console.error('Error searching products:', error);
        setProductSearchResults([]);
      } finally {
        setSearchingProducts(false);
      }
    }, 300); // Debounce 300ms

    return () => clearTimeout(timeoutId);
  }, [productSearchQuery, centerType, siteId]);

  // Fetch semantic map data
  useEffect(() => {
    const fetchData = async () => {
      try {
        setLoading(true);
        setError(null);

        const queryParams = new URLSearchParams({
          limit: '1000'
        });

        // Priority: centerProductId > centerQuestionId > centerTerm
        if (centerType === 'product' && selectedProduct) {
          queryParams.append('centerProductId', selectedProduct.id);
        } else if (centerQuestionId) {
          queryParams.append('centerQuestionId', centerQuestionId.toString());
        } else {
          queryParams.append('centerTerm', centerTerm);
        }

        // Add filters
        if (sourceFilter && sourceFilter !== 'all') {
          queryParams.append('sourceFilter', sourceFilter);
        }
        if (answerTypeFilter && answerTypeFilter !== 'all') {
          queryParams.append('answerTypeFilter', answerTypeFilter);
        }

        // Use sector endpoint if sectorId is provided, otherwise use question-wheels endpoint
        const endpoint = sectorId
          ? `/api/sectors/${sectorId}/question-wheels/${wheelId}/semantic-map?${queryParams}`
          : `/api/question-wheels/${wheelId}/semantic-map?${queryParams}`;

        const response = await authFetch(endpoint);

        if (!response.ok) {
          // Try to get actual error message from API response
          let errorMessage = 'Failed to fetch semantic map data';
          try {
            const errorData = await response.json();
            if (errorData.error) {
              errorMessage = errorData.error;
            }
          } catch {
            // JSON parse failed, use default message
          }
          throw new Error(errorMessage);
        }

        const mapData = await response.json();
        setData(mapData);

      } catch (err) {
        console.error('Error fetching semantic map:', err);
        setError(err instanceof Error ? err.message : 'Failed to load semantic map');
      } finally {
        setLoading(false);
      }
    };

    if (wheelId) {
      fetchData();
    }
  }, [wheelId, sectorId, centerTerm, centerQuestionId, sourceFilter, answerTypeFilter, centerType, selectedProduct]);

  // Calculate color based on similarity
  const getSimilarityColor = (similarity: number) => {
    // High similarity = green, low similarity = red
    const r = Math.round(255 * (1 - similarity));
    const g = Math.round(255 * similarity);
    return `rgb(${r}, ${g}, 50)`;
  };

  // Get question size based on search volume
  const getQuestionSize = (searchVolume: number | undefined) => {
    // Normalize size between 4 and 12 pixels
    const minSize = 4;
    const maxSize = 12;

    // Handle undefined or zero search volume
    if (!searchVolume || searchVolume === 0) {
      return minSize;
    }

    const maxVolume = Math.max(...(data?.questions.map(q => q.search_volume || 0).filter(v => v > 0) || [1]));

    // Prevent division by zero
    if (maxVolume === 0) {
      return minSize;
    }

    return minSize + ((searchVolume / maxVolume) * (maxSize - minSize));
  };

  // Handle question click - show details in side panel
  const handleQuestionClick = (question: Question) => {
    setSelectedQuestion(question);
  };

  // Make a question the center of the map
  const handleMakeCenter = (question: Question) => {
    setCenterQuestionId(question.id);
    setSelectedQuestion(null); // Clear selection when re-centering
  };

  // Reset to original center
  const handleResetCenter = () => {
    setCenterQuestionId(null);
  };

  // Zoom controls
  const handleZoomIn = () => setZoom(Math.min(zoom + 0.2, 3));
  const handleZoomOut = () => setZoom(Math.max(zoom - 0.2, 0.5));
  const handleResetZoom = () => setZoom(1);

  // Filter questions by similarity threshold
  const filteredQuestions = useMemo(() => {
    if (!data) return [];
    return data.questions.filter(q => q.similarity >= similarityThreshold / 100);
  }, [data, similarityThreshold]);

  // Calculate similarity statistics
  const similarityStats = useMemo(() => {
    if (!data) return null;

    const similarities = data.questions.map(q => q.similarity);
    const high = similarities.filter(s => s >= 0.8).length;
    const medium = similarities.filter(s => s >= 0.5 && s < 0.8).length;
    const low = similarities.filter(s => s < 0.5).length;

    return { high, medium, low };
  }, [data]);

  // Handler for "Go Answer Them" button
  const handleGoAnswerThem = () => {
    const questionIds = filteredQuestions.map(q => q.id.toString());

    navigate('/answer-generation', {
      state: {
        fromSemanticMap: true,
        wheelId,
        filteredQuestionIds: questionIds,
        filterContext: {
          similarityThreshold,
          centerQuestion: data?.center,
          centerTerm,
          sourceFilter,
          answerTypeFilter
        }
      }
    });
  };

  // Handler for "Create Product-Specific Wheel" button
  const handleCreateProductWheel = async () => {
    if (!selectedProduct || centerType !== 'product') {
      return;
    }

    try {
      setCreatingProductWheel(true);

      const response = await authFetch(
        `/api/sites/${siteId}/product-question-wheel`,
        {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            productId: selectedProduct.id,
            productName: selectedProduct.name,
            baseWheelId: wheelId,
            options: {
              maxQuestions: 100,
              focusAreas: ['specifications', 'comparisons', 'how-to', 'reviews']
            }
          })
        }
      );

      if (!response.ok) {
        const error = await response.json();
        throw new Error(error.error || 'Failed to create product-specific wheel');
      }

      const result = await response.json();

      // Show success message and navigate to the new wheel
      alert(`Success! Created product-specific wheel with ${result.totalQuestions} questions.\n\nNavigating to Question Wheel Research...`);

      // Navigate to the question wheel research page
      window.location.href = `/research/question-wheel`;

    } catch (error) {
      console.error('Error creating product-specific wheel:', error);
      alert(`Failed to create product-specific wheel: ${error instanceof Error ? error.message : 'Unknown error'}`);
    } finally {
      setCreatingProductWheel(false);
    }
  };

  // Fetch embedding stats when error mentions embeddings
  const fetchEmbeddingStats = async () => {
    try {
      const endpoint = sectorId
        ? `/api/sectors/${sectorId}/question-wheels/${wheelId}/embedding-stats`
        : `/api/question-wheels/${wheelId}/embedding-stats`;

      const response = await authFetch(endpoint);
      if (response.ok) {
        const stats = await response.json();
        setEmbeddingStats(stats);
      }
    } catch (err) {
      console.error('Error fetching embedding stats:', err);
    }
  };

  // Generate embeddings for the wheel
  const handleGenerateEmbeddings = async () => {
    try {
      setGeneratingEmbeddings(true);

      const endpoint = sectorId
        ? `/api/sectors/${sectorId}/question-wheels/${wheelId}/generate-embeddings`
        : `/api/question-wheels/${wheelId}/generate-embeddings`;

      const response = await authFetch(endpoint, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ force: false })
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.error || 'Failed to generate embeddings');
      }

      const result = await response.json();
      alert(`Successfully generated embeddings for ${result.generated} questions!\n\nThe semantic map will now reload.`);

      // Reset error and reload data
      setError(null);
      setLoading(true);

      // Force a re-fetch by updating a dependency
      window.location.reload();

    } catch (err) {
      console.error('Error generating embeddings:', err);
      alert(`Failed to generate embeddings: ${err instanceof Error ? err.message : 'Unknown error'}`);
    } finally {
      setGeneratingEmbeddings(false);
    }
  };

  if (loading) {
    return (
      <Card>
        <CardContent className="flex items-center justify-center py-12">
          <Loader2 className="h-8 w-8 animate-spin text-primary" />
          <span className="ml-2">Loading semantic map...</span>
        </CardContent>
      </Card>
    );
  }

  // Check if error is about missing embeddings
  const isEmbeddingError = error?.toLowerCase().includes('embedding') || error?.toLowerCase().includes('no questions with');

  if (error || !data) {
    // Fetch embedding stats when showing embedding error
    if (isEmbeddingError && !embeddingStats) {
      fetchEmbeddingStats();
    }

    return (
      <Card>
        <CardContent className="flex flex-col items-center justify-center py-12 space-y-4">
          <AlertTriangle className="h-12 w-12 text-amber-500" />
          <div className="text-center">
            <p className="text-lg font-medium text-foreground mb-2">
              {isEmbeddingError ? 'Embeddings Required' : 'Unable to Load Semantic Map'}
            </p>
            <p className="text-muted-foreground">
              {error || 'No data available'}
            </p>
          </div>

          {isEmbeddingError && (
            <div className="text-center space-y-3 mt-4">
              {embeddingStats && (
                <div className="text-sm text-muted-foreground">
                  <p>Total questions: {embeddingStats.totalQuestions}</p>
                  <p>With embeddings: {embeddingStats.withEmbeddings} ({embeddingStats.percentage}%)</p>
                  <p>Need embeddings: {embeddingStats.withoutEmbeddings}</p>
                </div>
              )}
              <Button
                onClick={handleGenerateEmbeddings}
                disabled={generatingEmbeddings}
                className="mt-4"
              >
                {generatingEmbeddings ? (
                  <>
                    <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                    Generating Embeddings...
                  </>
                ) : (
                  <>
                    <Sparkles className="h-4 w-4 mr-2" />
                    Generate Embeddings
                  </>
                )}
              </Button>
              <p className="text-xs text-muted-foreground max-w-md">
                This will generate semantic embeddings for all questions using AI.
                This may take a few minutes depending on the number of questions.
              </p>
            </div>
          )}
        </CardContent>
      </Card>
    );
  }

  // Handle center question interactions
  const handleCenterHover = () => {
    if (data?.center) {
      setHoveredQuestion(data.center as Question);
    }
  };

  const handleCenterLeave = () => {
    setHoveredQuestion(null);
  };

  const handleCenterClick = () => {
    if (data?.center) {
      setSelectedQuestion(data.center as Question);
    }
  };

  return (
    <div className="space-y-4">
      {/* Filter Controls */}
      <Card>
        <CardHeader className="pb-3">
          <CardTitle className="text-base">Semantic Center & Filters</CardTitle>
          <CardDescription className="text-sm">
            Choose semantic center type, apply filters, and navigate to answer generation
          </CardDescription>
        </CardHeader>
        <CardContent>
          <div className="space-y-4">
            {/* Center Type Selector */}
            <div className="space-y-2">
              <Label className="text-sm font-medium">Semantic Center Type</Label>
              <Tabs value={centerType} onValueChange={(value) => setCenterType(value as 'question' | 'product')}>
                <TabsList className="grid w-full grid-cols-2">
                  <TabsTrigger value="question" className="flex items-center gap-2">
                    <MessageSquare className="h-4 w-4" />
                    Question
                  </TabsTrigger>
                  <TabsTrigger value="product" className="flex items-center gap-2">
                    <Package className="h-4 w-4" />
                    Product
                  </TabsTrigger>
                </TabsList>
              </Tabs>
              <p className="text-xs text-muted-foreground">
                {centerType === 'question'
                  ? 'Questions positioned by similarity to the center question'
                  : 'Questions positioned by similarity to a selected product'}
              </p>
            </div>

            {/* Product Search (only shown when centerType is 'product') */}
            {centerType === 'product' && (
              <div className="space-y-2">
                <Label className="text-sm font-medium">Search for Product</Label>
                <div className="relative">
                  <Input
                    placeholder="Type product name to search..."
                    value={productSearchQuery}
                    onChange={(e) => setProductSearchQuery(e.target.value)}
                    className="pr-10"
                  />
                  {searchingProducts && (
                    <Loader2 className="absolute right-3 top-3 h-4 w-4 animate-spin text-muted-foreground" />
                  )}
                </div>

                {/* Product search results */}
                {productSearchResults.length > 0 && (
                  <div className="border rounded-md max-h-60 overflow-y-auto">
                    {productSearchResults.map((product) => (
                      <button
                        key={product.id}
                        onClick={() => {
                          setSelectedProduct(product);
                          setProductSearchQuery(product.name);
                          setProductSearchResults([]);
                        }}
                        className="w-full text-left px-3 py-2 hover:bg-accent transition-colors flex items-center justify-between"
                      >
                        <div className="flex-1">
                          <div className="font-medium text-sm">{product.name}</div>
                          {product.brand && (
                            <div className="text-xs text-muted-foreground">{product.brand}</div>
                          )}
                        </div>
                        {!product.has_embedding && (
                          <Badge variant="outline" className="text-xs">No embedding</Badge>
                        )}
                      </button>
                    ))}
                  </div>
                )}

                {/* Selected product display */}
                {selectedProduct && (
                  <div className="p-3 bg-muted rounded-md flex items-center justify-between">
                    <div className="flex-1">
                      <div className="font-medium text-sm">{selectedProduct.name}</div>
                      {selectedProduct.brand && (
                        <div className="text-xs text-muted-foreground">{selectedProduct.brand}</div>
                      )}
                    </div>
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => {
                        setSelectedProduct(null);
                        setProductSearchQuery('');
                      }}
                    >
                      Clear
                    </Button>
                  </div>
                )}
              </div>
            )}

            {/* Similarity Threshold Slider */}
            <div className="space-y-2">
              <div className="flex justify-between items-center">
                <label className="text-sm font-medium">
                  Similarity Threshold: {similarityThreshold}%
                </label>
                <span className="text-sm text-muted-foreground">
                  Showing {filteredQuestions.length} of {data.metadata.totalQuestions} questions
                </span>
              </div>
              <Slider
                value={[similarityThreshold]}
                onValueChange={(values) => setSimilarityThreshold(values[0])}
                max={100}
                step={1}
                className="w-full"
              />
              <p className="text-xs text-muted-foreground">
                Filter out questions below {similarityThreshold}% semantic similarity to the center question
              </p>
            </div>

            {/* Action Buttons */}
            <div className="space-y-2">
              <div className="flex gap-2">
                <Button
                  onClick={handleGoAnswerThem}
                  disabled={filteredQuestions.length === 0}
                  className="flex-1"
                >
                  <ArrowRight className="h-4 w-4 mr-2" />
                  Go Answer {filteredQuestions.length > 0 ? `${filteredQuestions.length} Questions` : 'Them'}
                </Button>
                {similarityThreshold > 0 && (
                  <Button
                    variant="outline"
                    onClick={() => setSimilarityThreshold(0)}
                  >
                    Clear Filter
                  </Button>
                )}
              </div>

              {/* Product-Specific Wheel Generator */}
              {centerType === 'product' && selectedProduct && (
                <Button
                  onClick={handleCreateProductWheel}
                  disabled={creatingProductWheel || filteredQuestions.length === 0}
                  variant="secondary"
                  className="w-full"
                >
                  {creatingProductWheel ? (
                    <>
                      <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                      Creating Product-Specific Wheel...
                    </>
                  ) : (
                    <>
                      <Package className="h-4 w-4 mr-2" />
                      Create Product-Specific Wheel ({filteredQuestions.length} questions)
                    </>
                  )}
                </Button>
              )}
              {centerType === 'product' && selectedProduct && (
                <p className="text-xs text-muted-foreground">
                  This will reformulate {filteredQuestions.length} generic questions to be specific to "{selectedProduct.name}" using AI.
                </p>
              )}
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Stats */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-sm font-medium text-muted-foreground flex items-center gap-2">
              {data.metadata.centerType === 'product' ? (
                <>
                  <Package className="h-4 w-4" />
                  Center Product
                </>
              ) : (
                <>
                  <MessageSquare className="h-4 w-4" />
                  Center Question
                </>
              )}
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="text-sm font-medium truncate" title={'centerType' in data.center ? data.center.name : data.center.text}>
              {'centerType' in data.center ? data.center.name : data.center.text}
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-sm font-medium text-muted-foreground">
              Related Questions
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">{data.metadata.totalQuestions}</div>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-sm font-medium text-muted-foreground">
              Avg Similarity
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">
              {(data.metadata.avgSimilarity * 100).toFixed(0)}%
            </div>
          </CardContent>
        </Card>

        {similarityStats && (
          <Card>
            <CardHeader className="pb-2">
              <CardTitle className="text-sm font-medium text-muted-foreground">
                Similarity Distribution
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="flex gap-2 text-xs">
                <Badge variant="default" className="bg-green-600">
                  High: {similarityStats.high}
                </Badge>
                <Badge variant="default" className="bg-yellow-600">
                  Med: {similarityStats.medium}
                </Badge>
                <Badge variant="default" className="bg-red-600">
                  Low: {similarityStats.low}
                </Badge>
              </div>
            </CardContent>
          </Card>
        )}
      </div>

      {/* Visualization with Side Panel */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
        {/* Main Visualization - 2/3 width */}
        <Card className="lg:col-span-2">
          <CardHeader>
            <div className="flex justify-between items-center">
              <div>
                <CardTitle>Semantic Question Map</CardTitle>
                <CardDescription>
                  Questions positioned by semantic relationships. Click to select, hover for visual feedback.
                </CardDescription>
              </div>
              <div className="flex gap-2">
                {centerQuestionId && (
                  <Button variant="outline" size="sm" onClick={handleResetCenter}>
                    Reset Center
                  </Button>
                )}
                <Button variant="outline" size="icon" onClick={handleZoomOut}>
                  <ZoomOut className="h-4 w-4" />
                </Button>
                <Button variant="outline" size="icon" onClick={handleResetZoom}>
                  <Maximize2 className="h-4 w-4" />
                </Button>
                <Button variant="outline" size="icon" onClick={handleZoomIn}>
                  <ZoomIn className="h-4 w-4" />
                </Button>
              </div>
            </div>
          </CardHeader>
          <CardContent>
            <div className="relative bg-slate-50 dark:bg-slate-950 rounded-lg overflow-hidden">
            <svg
              width={svgWidth}
              height={svgHeight}
              viewBox={`0 0 ${svgWidth} ${svgHeight}`}
              className="border rounded-lg"
            >
              {/* Apply zoom transform */}
              <g transform={`translate(${centerX}, ${centerY}) scale(${zoom}) translate(${-centerX}, ${-centerY})`}>
                {/* Concentric circles for reference */}
                {[0.25, 0.5, 0.75, 1].map((factor) => (
                  <circle
                    key={factor}
                    cx={centerX}
                    cy={centerY}
                    r={300 * factor}
                    fill="none"
                    stroke="currentColor"
                    strokeWidth="1"
                    strokeDasharray="4 4"
                    opacity="0.2"
                  />
                ))}

                {/* Lines from center to questions */}
                {filteredQuestions.map((q) => (
                  <line
                    key={`line-${q.id}`}
                    x1={centerX}
                    y1={centerY}
                    x2={centerX + q.position.x}
                    y2={centerY + q.position.y}
                    stroke="currentColor"
                    strokeWidth="0.5"
                    opacity="0.1"
                  />
                ))}

                {/* Question nodes */}
                {filteredQuestions.map((q) => {
                  const size = getQuestionSize(q.search_volume);
                  return (
                    <circle
                      key={`question-${q.id}`}
                      cx={centerX + q.position.x}
                      cy={centerY + q.position.y}
                      r={size}
                      fill={getSimilarityColor(q.similarity)}
                      stroke="white"
                      strokeWidth="1.5"
                      className="cursor-pointer transition-all hover:r-[16px]"
                      onMouseEnter={() => setHoveredQuestion(q)}
                      onMouseLeave={() => setHoveredQuestion(null)}
                      onClick={() => handleQuestionClick(q)}
                    />
                  );
                })}

                {/* Center question */}
                <circle
                  cx={centerX}
                  cy={centerY}
                  r={16}
                  fill="hsl(var(--primary))"
                  stroke="white"
                  strokeWidth="3"
                  className="cursor-pointer"
                  onMouseEnter={handleCenterHover}
                  onMouseLeave={handleCenterLeave}
                  onClick={handleCenterClick}
                />
                <text
                  x={centerX}
                  y={centerY}
                  textAnchor="middle"
                  dominantBaseline="middle"
                  className="text-xs font-bold fill-white pointer-events-none"
                >
                  {data.metadata.centerType === 'product' ? 'P' : data.center.type?.charAt(0).toUpperCase() || 'Q'}
                </text>
              </g>
            </svg>
          </div>

          {/* Legend */}
          <div className="mt-4 flex justify-between items-center text-xs text-muted-foreground">
            <div className="flex items-center gap-4">
              <div className="flex items-center gap-2">
                <div className="w-3 h-3 rounded-full" style={{ backgroundColor: getSimilarityColor(1) }} />
                <span>High similarity</span>
              </div>
              <div className="flex items-center gap-2">
                <div className="w-3 h-3 rounded-full" style={{ backgroundColor: getSimilarityColor(0.5) }} />
                <span>Medium similarity</span>
              </div>
              <div className="flex items-center gap-2">
                <div className="w-3 h-3 rounded-full" style={{ backgroundColor: getSimilarityColor(0) }} />
                <span>Low similarity</span>
              </div>
            </div>
            <div>
              Circle size = search volume
            </div>
          </div>

          {/* Explanation */}
          <div className="mt-4 p-4 bg-blue-50 dark:bg-blue-950 rounded-lg border border-blue-200 dark:border-blue-800">
            <h4 className="text-sm font-semibold text-blue-900 dark:text-blue-100 mb-2">
              How this visualization works
            </h4>
            <p className="text-xs text-blue-800 dark:text-blue-200 leading-relaxed">
              This map uses <strong>semantic analysis</strong> to position questions based on their meaning.
              Questions that are close together share similar concepts and context. The visualization uses PCA
              (Principal Component Analysis) to reduce the 1,536-dimensional AI embeddings into a 2D space,
              creating natural clusters of related questions. The <strong>similarity percentage</strong> shown
              in the details panel indicates how closely related each question is to the center question.
              Click any question to re-center and explore different semantic perspectives.
            </p>
          </div>
          </CardContent>
        </Card>

        {/* Question Details Panel - 1/3 width */}
        <Card className="lg:col-span-1">
          <CardHeader>
            <CardTitle className="text-sm">Question Details</CardTitle>
            <CardDescription className="text-xs">
              {selectedQuestion ? 'Selected question details' : 'Click on a question to see details'}
            </CardDescription>
          </CardHeader>
          <CardContent>
            {selectedQuestion ? (
              <div className="space-y-4">
                <div>
                  <div className="font-medium text-sm mb-2">{selectedQuestion.text}</div>
                  {selectedQuestion.type && (
                    <Badge variant="secondary" className="text-xs">
                      {selectedQuestion.type.toUpperCase()}
                    </Badge>
                  )}
                </div>

                {/* Metrics Grid */}
                <div className="grid grid-cols-2 gap-3">
                  <div className="bg-muted rounded-lg p-2">
                    <div className="text-xs text-muted-foreground">Search Volume</div>
                    <div className="text-lg font-bold">
                      {(selectedQuestion.search_volume || 0).toLocaleString()}
                    </div>
                  </div>
                  <div className="bg-muted rounded-lg p-2">
                    <div className="text-xs text-muted-foreground">CPC</div>
                    <div className="text-lg font-bold">
                      ${Number(selectedQuestion.cpc || 0).toFixed(2)}
                    </div>
                  </div>
                  <div className="bg-muted rounded-lg p-2">
                    <div className="text-xs text-muted-foreground">Opportunity</div>
                    <div className="text-lg font-bold text-green-600">
                      {selectedQuestion.opportunity_score || 0}
                    </div>
                  </div>
                  <div className="bg-muted rounded-lg p-2">
                    <div className="text-xs text-muted-foreground">Difficulty</div>
                    <div className="text-lg font-bold text-orange-600">
                      {selectedQuestion.difficulty_score || 0}
                    </div>
                  </div>
                </div>

                {/* Similarity */}
                {selectedQuestion.similarity !== undefined && (
                  <div>
                    <div className="text-xs text-muted-foreground mb-1">Similarity to Center</div>
                    <div className="flex items-center gap-2">
                      <div className="flex-1 bg-muted rounded-full h-2">
                        <div
                          className="h-2 rounded-full"
                          style={{
                            width: `${(selectedQuestion.similarity || 0) * 100}%`,
                            backgroundColor: getSimilarityColor(selectedQuestion.similarity || 0)
                          }}
                        />
                      </div>
                      <span className="text-sm font-medium">
                        {((selectedQuestion.similarity || 0) * 100).toFixed(0)}%
                      </span>
                    </div>
                  </div>
                )}

                {/* Answer Type */}
                {selectedQuestion.answer_type && (
                  <div>
                    <div className="text-xs text-muted-foreground mb-1">Answer Type</div>
                    <Badge variant="default" className="text-xs">
                      {selectedQuestion.answer_type.replace(/_/g, ' ')}
                    </Badge>
                    {selectedQuestion.answer_type_confidence && (
                      <span className="text-xs text-muted-foreground ml-2">
                        {(selectedQuestion.answer_type_confidence * 100).toFixed(0)}% confidence
                      </span>
                    )}
                  </div>
                )}

                {/* SERP Features */}
                {(selectedQuestion.has_featured_snippet || selectedQuestion.has_paa) && (
                  <div>
                    <div className="text-xs text-muted-foreground mb-1">SERP Features</div>
                    <div className="flex gap-1 flex-wrap">
                      {selectedQuestion.has_featured_snippet && (
                        <Badge variant="secondary" className="text-xs">Featured Snippet</Badge>
                      )}
                      {selectedQuestion.has_paa && (
                        <Badge variant="secondary" className="text-xs">PAA</Badge>
                      )}
                    </div>
                  </div>
                )}

                {/* Action Buttons */}
                <div className="flex flex-col gap-2">
                  <Button
                    variant="default"
                    size="sm"
                    className="w-full"
                    onClick={() => handleMakeCenter(selectedQuestion)}
                  >
                    Make it Center
                  </Button>
                  <Button
                    variant="outline"
                    size="sm"
                    className="w-full"
                    onClick={() => {
                      if (onQuestionClick) {
                        onQuestionClick(selectedQuestion);
                      }
                    }}
                  >
                    View Full Details
                  </Button>
                </div>
              </div>
            ) : (
              <div className="text-center py-8 text-muted-foreground text-sm">
                <div className="mb-2">🖱️</div>
                <p>Click on any question to see its details here</p>
              </div>
            )}
          </CardContent>
        </Card>
      </div>
    </div>
  );
};
